import { type FC, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
	VIDEO_GENERATION_MAX_ATTACHMENTS,
	VIDEO_PLATFORM_RULES,
	VIDEO_PROMPT_MAX_CHARS,
	type VideoGenerationDto,
	type VideoOrientation,
	type VideoPresenterOption,
} from "shared";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { Modal, useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Spinner } from "../common/Spinner";
import { Button } from "../components/Button";
import { ModalSelectMedia } from "../post/ModalSelectMedia";
import {
	formatMinutes,
	useVideoActions,
	useVideoOptions,
} from "./useVideoGenerations";

const PLATFORM_LABELS: Record<string, string> = {
	instagram: "Instagram",
	tiktok: "TikTok",
	youtube: "YouTube Shorts",
	facebook: "Facebook",
	threads: "Threads",
	linkedin: "LinkedIn",
	x: "X",
};

interface Props {
	/** Make a video for this post; omit for a standalone video. */
	postId?: number;
	onStarted?: (video: VideoGenerationDto) => void;
}

function PresenterSelect(props: {
	id: string;
	label: string;
	value: string;
	options: VideoPresenterOption[];
	onChange: (value: string) => void;
}) {
	return (
		<Form.Group className="mb-2">
			<Form.Label htmlFor={props.id}>{props.label}</Form.Label>
			<ExtraForm.Select
				id={props.id}
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			>
				<option value="">Let Moxie pick</option>
				{props.options.map((o) => (
					<option key={o.id} value={o.id}>
						{o.detail ? `${o.name} · ${o.detail}` : o.name}
					</option>
				))}
			</ExtraForm.Select>
		</Form.Group>
	);
}

function ScriptSection(props: { title: string; body: string }) {
	return (
		<div className="mb-3">
			<div className="fw-semibold mb-1">{props.title}</div>
			<div className="text-muted" style={{ whiteSpace: "pre-line" }}>
				{props.body}
			</div>
		</div>
	);
}

export const ModalMakeVideo: FC<Props> = ({ postId, onStarted }) => {
	const { close, open } = useModal();
	const emit = useAlertEmit();
	const { preview, start } = useVideoActions();
	const [topic, setTopic] = useState("");
	const [platform, setPlatform] = useState("");
	const [draft, setDraft] = useState<VideoGenerationDto | null>(null);
	const [advanced, setAdvanced] = useState(false);
	const [prompt, setPrompt] = useState("");
	const [orientation, setOrientation] = useState<VideoOrientation>("portrait");
	const [attachments, setAttachments] = useState<number[]>([]);
	const [avatarId, setAvatarId] = useState("");
	const [voiceId, setVoiceId] = useState("");
	const [styleId, setStyleId] = useState("");
	const [saveAsDefaults, setSaveAsDefaults] = useState(false);
	const options = useVideoOptions(draft !== null);
	const autoStarted = useRef(false);

	const writeScript = () =>
		preview.mutate(
			postId
				? { postId }
				: { topic: topic.trim(), platform: platform || undefined },
			{
				onSuccess: (dto) => {
					setDraft(dto);
					setPrompt(dto.prompt);
					setOrientation(dto.orientation);
					setAvatarId(dto.avatarId ?? "");
					setVoiceId(dto.voiceId ?? "");
					setStyleId(dto.styleId ?? "");
				},
			},
		);

	// biome-ignore lint/correctness/useExhaustiveDependencies: write the script once when opened from a post
	useEffect(() => {
		if (postId && !autoStarted.current) {
			autoStarted.current = true;
			writeScript();
		}
	}, [postId]);

	const pickAttachments = () =>
		open(ModalSelectMedia, {
			values: attachments,
			multiple: true,
			mediaHint: `Pick up to ${VIDEO_GENERATION_MAX_ATTACHMENTS} photos or MP4 clips of your real business. They're used as the main visuals.`,
			onChange: (ids: number[]) => {
				if (ids.length > VIDEO_GENERATION_MAX_ATTACHMENTS) {
					emit({
						type: AlertType.WARNING,
						content: `Only the first ${VIDEO_GENERATION_MAX_ATTACHMENTS} were kept.`,
					});
				}
				setAttachments(ids.slice(0, VIDEO_GENERATION_MAX_ATTACHMENTS));
			},
		});

	const generate = () => {
		if (!draft) {
			return;
		}
		start.mutate(
			{
				id: draft.id,
				dto: {
					prompt: advanced ? prompt : undefined,
					orientation,
					attachments,
					avatarId: avatarId || null,
					voiceId: voiceId || null,
					styleId: styleId || null,
					saveAsDefaults,
				},
			},
			{
				onSuccess: (video) => {
					emit({
						type: AlertType.SUCCESS,
						content: postId
							? "Your video is being made. We'll add it to this post when it's ready."
							: "Your video is being made. It will appear in your media library when it's ready.",
					});
					onStarted?.(video);
					close();
				},
			},
		);
	};

	const error = preview.error ?? start.error;
	const promptTooLong = prompt.length > VIDEO_PROMPT_MAX_CHARS;

	return (
		<Modal size="lg">
			<h4 className="mb-1">Make a video</h4>
			<p className="text-muted mb-3">
				Moxie writes a short script from your business details. Review it, then
				we'll turn it into a captioned video.
			</p>

			{error && (
				<div className="alert alert-danger py-2" role="alert">
					{parseError(error) ?? "Something went wrong. Try again."}
				</div>
			)}

			{!draft && !postId && (
				<>
					<Form.Group className="mb-2">
						<Form.Label htmlFor="video-topic">
							What should the video be about?
						</Form.Label>
						<ExtraForm.Control
							id="video-topic"
							value={topic}
							maxLength={500}
							onChange={(e) => setTopic(e.target.value)}
							placeholder="e.g. Our spring AC tune-up special"
						/>
					</Form.Group>
					<Form.Group className="mb-3">
						<Form.Label htmlFor="video-platform">
							Where will you post it?
						</Form.Label>
						<ExtraForm.Select
							id="video-platform"
							value={platform}
							onChange={(e) => setPlatform(e.target.value)}
						>
							<option value="">Anywhere</option>
							{Object.keys(VIDEO_PLATFORM_RULES).map((p) => (
								<option key={p} value={p}>
									{PLATFORM_LABELS[p] ?? p}
								</option>
							))}
						</ExtraForm.Select>
					</Form.Group>
					<Button
						full
						onClick={writeScript}
						loading={preview.isPending}
						disabled={!topic.trim()}
					>
						Write the script
					</Button>
				</>
			)}

			{!draft && postId && (
				<div className="d-flex flex-column align-items-center gap-2 py-4">
					{preview.isPending && <Spinner />}
					<span className="text-muted">
						{preview.isPending
							? "Writing a script from your post…"
							: "Couldn't write the script."}
					</span>
					{preview.isError && (
						<Button variant="secondary" onClick={writeScript}>
							Try again
						</Button>
					)}
				</div>
			)}

			{draft && (
				<>
					{advanced ? (
						<Form.Group className="mb-3">
							<Form.Label htmlFor="video-prompt">Script</Form.Label>
							<Form.Control
								as="textarea"
								id="video-prompt"
								rows={12}
								value={prompt}
								onChange={(e) => setPrompt(e.target.value)}
								isInvalid={promptTooLong || !prompt.trim()}
							/>
							<Form.Text
								className={promptTooLong ? "text-danger" : "text-muted"}
							>
								{prompt.length.toLocaleString()} /{" "}
								{VIDEO_PROMPT_MAX_CHARS.toLocaleString()} characters
							</Form.Text>
						</Form.Group>
					) : (
						<div className="p-3 border rounded mb-3">
							<ScriptSection
								title="What it says"
								body={draft.sections.whatItSays}
							/>
							<ScriptSection
								title="How it looks"
								body={draft.sections.howItLooks}
							/>
							<ScriptSection
								title="Length and format"
								body={draft.sections.lengthAndFormat}
							/>
						</div>
					)}
					<Form.Check
						type="switch"
						id="video-advanced"
						className="mb-3"
						label="Edit the full script (advanced)"
						checked={advanced}
						onChange={() => setAdvanced((v) => !v)}
					/>

					<Form.Group className="mb-3">
						<Form.Label htmlFor="video-orientation">Shape</Form.Label>
						<ExtraForm.Select
							id="video-orientation"
							value={orientation}
							onChange={(e) =>
								setOrientation(e.target.value as VideoOrientation)
							}
						>
							<option value="portrait">
								Portrait (9:16) · Reels, Stories, Shorts
							</option>
							<option value="landscape">
								Landscape (16:9) · LinkedIn, X, YouTube
							</option>
						</ExtraForm.Select>
					</Form.Group>

					<div className="mb-3">
						<div className="fw-semibold mb-1">Your photos and clips</div>
						<div className="d-flex align-items-center gap-2">
							<Button
								variant="secondary"
								size="sm"
								icon="add_photo_alternate"
								onClick={pickAttachments}
							>
								{attachments.length ? "Change" : "Add photos or clips"}
							</Button>
							<span className="text-muted">
								{attachments.length
									? `${attachments.length} attached`
									: "Optional. Real photos make the video feel like your business."}
							</span>
							{attachments.length > 0 && (
								<Button
									variant="link"
									size="sm"
									onClick={() => setAttachments([])}
								>
									Remove
								</Button>
							)}
						</div>
					</div>

					{options.isLoading && <Spinner />}
					{options.data && (
						<>
							<PresenterSelect
								id="video-avatar"
								label="Presenter"
								value={avatarId}
								options={options.data.avatars}
								onChange={setAvatarId}
							/>
							<PresenterSelect
								id="video-voice"
								label="Voice"
								value={voiceId}
								options={options.data.voices}
								onChange={setVoiceId}
							/>
							<PresenterSelect
								id="video-style"
								label="Style"
								value={styleId}
								options={options.data.styles}
								onChange={setStyleId}
							/>
							<Form.Check
								id="video-save-defaults"
								className="mb-3"
								label="Use these for my next videos"
								checked={saveAsDefaults}
								onChange={() => setSaveAsDefaults((v) => !v)}
							/>
						</>
					)}
					{options.isError && (
						<p className="text-muted">
							Presenter choices couldn't load, so Moxie will pick for you.
						</p>
					)}

					<p className="text-muted mb-3">
						About {draft.targetSeconds} seconds long. Usually ready in about{" "}
						{formatMinutes(draft.estimatedRenderSeconds)}. You're only charged
						for videos that finish.
					</p>

					<div className="d-flex gap-2">
						<Button full variant="secondary" onClick={() => close()}>
							Cancel
						</Button>
						<Button
							full
							icon="movie"
							onClick={generate}
							loading={start.isPending}
							disabled={advanced && (promptTooLong || !prompt.trim())}
						>
							Make the video
						</Button>
					</div>
				</>
			)}
		</Modal>
	);
};
